import { Box, Button, Typography, alpha } from '@mui/material'
import { useState, useRef, useEffect } from 'react'
import MuxPlayer from '@mux/mux-player-react'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { HeroSlide } from 'src/v3/core/types'
import { useHeroCarouselTimer } from './hooks/use-hero-carousel-timer'

export let HeroCarousel = ({
  slides,
  onNavigate,
}: {
  slides: HeroSlide[]
  onNavigate: (path: string) => void
}) => {
  let [currentSlide, setCurrentSlide] = useState(0)
  let [isHovering, setIsHovering] = useState(false)
  let [showVideo, setShowVideo] = useState(false)
  let [isPlaying, setIsPlaying] = useState(false)
  let muxPlayerRef = useRef<any>(null)
  let videoCompletedRef = useRef(false)

  let currentSlideData = slides[currentSlide]

  // Smart timer that pauses during hover
  let { resetTimer } = useHeroCarouselTimer({
    slideCount: slides.length,
    isHovering,
    onSlideChange: () => {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    },
  })

  // Show video only when hovering
  useEffect(() => {
    if (isHovering && currentSlideData?.videoSrc) {
      setShowVideo(true)
      setIsPlaying(true)
    } else {
      setShowVideo(false)
      setIsPlaying(false)
    }
  }, [isHovering, currentSlideData, currentSlide])

  // Handle video playback and looping with timestamps
  useEffect(() => {
    if (showVideo && muxPlayerRef.current && currentSlideData?.videoSrc) {
      let player = muxPlayerRef.current

      let handleTimeUpdate = () => {
        if (
          currentSlideData.endTime &&
          player.currentTime >= currentSlideData.endTime
        ) {
          // Video reached end time
          if (!videoCompletedRef.current) {
            // eslint-disable-next-line fp/no-mutation
            videoCompletedRef.current = true
            // If still hovering, auto-advance to next slide
            if (isHovering && slides.length > 1) {
              setCurrentSlide((prev) => (prev + 1) % slides.length)
              resetTimer()
            }
          }
          // Loop the video back to start
          // eslint-disable-next-line fp/no-mutation
          player.currentTime = currentSlideData.startTime || 0
          // eslint-disable-next-line fp/no-mutation
          videoCompletedRef.current = false
        }
      }

      let handleLoadedData = () => {
        if (currentSlideData.startTime) {
          // eslint-disable-next-line fp/no-mutation
          player.currentTime = currentSlideData.startTime
        }
        if (isPlaying) {
          player.play().catch(() => {})
        }
      }

      player.addEventListener('timeupdate', handleTimeUpdate)
      player.addEventListener('loadeddata', handleLoadedData)

      return () => {
        player.removeEventListener('timeupdate', handleTimeUpdate)
        player.removeEventListener('loadeddata', handleLoadedData)
      }
    }
    return undefined
  }, [
    showVideo,
    isPlaying,
    currentSlideData,
    isHovering,
    slides.length,
    resetTimer,
  ])

  let handleMouseEnter = () => {
    setIsHovering(true)
  }

  let handleMouseLeave = () => {
    setIsHovering(false)
  }

  let handleSlideChange = (index: number) => {
    resetTimer()
    setCurrentSlide(index)
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: {
          xs: '40vh',
          sm: '48vh',
          md: '56vh',
        },
        minHeight: {
          xs: '240px',
          sm: '320px',
          md: '400px',
        },
        overflow: 'hidden',
        borderRadius: 0,
        mb: 4,
        cursor: 'default',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Background Image */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${import.meta.env.VITE_CLOUDFRONT_URL}/public/${currentSlideData?.imageName})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'opacity 0.3s ease-in-out',
          opacity: showVideo ? 0 : 1,
        }}
      />

      {/* Mux Video Player */}
      {showVideo && currentSlideData?.videoSrc && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            animation: 'fadeIn 0.3s ease-in-out forwards',
            '@keyframes fadeIn': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 },
            },
            '& mux-player': {
              width: '100%',
              height: '100%',
              '--controls': 'none',
              '--aspect-ratio': 'none',
              '--media-object-fit': 'cover',
              '--media-object-position': 'center',
              '& video': {
                objectFit: 'cover !important',
                objectPosition: 'center !important',
                width: '100% !important',
                height: '100% !important',
                minWidth: '100% !important',
                minHeight: '100% !important',
              },
            },
          }}
        >
          <MuxPlayer
            ref={muxPlayerRef}
            src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${currentSlideData.videoSrc}`}
            muted
            autoPlay
            playsInline
            preload="none"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={() => {
              setShowVideo(false)
            }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
        </Box>
      )}

      {/* Hover indicator for slides with videos */}
      {!showVideo && currentSlideData?.videoSrc && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            opacity: isHovering ? 0 : 0.4,
            transition: 'opacity 0.3s ease',
            pointerEvents: 'none',
            backgroundColor: 'rgba(0,0,0,0.5)',
            borderRadius: '50%',
            p: 1,
          }}
        >
          <PlayArrowIcon sx={{ fontSize: 48, color: 'white' }} />
        </Box>
      )}

      {/* Gradient Overlay - fade to pure black */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '65%',
          background:
            'linear-gradient(transparent, rgba(0,0,0,0.5), rgba(0,0,0,0.9), #000000)',
          pointerEvents: 'none',
        }}
      />

      {/* Slide Indicators */}
      {slides.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: {
              xs: 80,
              sm: 90,
              md: 100,
            },
            right: {
              xs: 16,
              sm: 20,
              md: 24,
            },
            display: 'flex',
            gap: 1,
            pointerEvents: 'auto',
          }}
        >
          {slides.map((_, index: number) => (
            <Box
              key={index}
              onClick={(e) => {
                e.stopPropagation()
                handleSlideChange(index)
              }}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor:
                  index === currentSlide ? 'white' : 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.8)',
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Content Overlay */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          p: {
            xs: 2,
            sm: 3,
            md: 4,
          },
          color: 'white',
          pointerEvents: 'none',
        }}
      >
        <Typography
          variant="h2"
          sx={{
            mb: 2,
            maxWidth: {
              xs: '90%',
              sm: '70%',
              md: '60%',
            },
            fontSize: {
              xs: '1.5rem',
              sm: '1.75rem',
              md: '2.25rem',
            },
            fontWeight: 400,
            letterSpacing: '-0.02em',
          }}
        >
          {currentSlideData?.title}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mb: 3,
            opacity: 0.85,
            maxWidth: {
              xs: '90%',
              sm: '60%',
              md: '50%',
            },
            fontSize: {
              xs: '0.875rem',
              sm: '1rem',
              md: '1.125rem',
            },
            fontWeight: 300,
            lineHeight: 1.5,
          }}
        >
          {currentSlideData?.description}
        </Typography>
        <Button
          variant="contained"
          size="large"
          color="secondary"
          startIcon={<PlayArrowIcon />}
          onClick={(e) => {
            e.stopPropagation()
            if (currentSlideData?.seriesID) {
              onNavigate(`/self-paced/series/${currentSlideData.seriesID}`)
            }
            // We don't support individual courses in the carousel
            // but when we add it, we can handle it here
            // else if (currentSlideData?.courseId) {
            //   onNavigate(`/self-paced/course/${currentSlideData.courseId}`)
            // }
            else {
              onNavigate('/self-paced')
            }
          }}
          sx={{
            px: {
              xs: 3,
              sm: 4,
              md: 4,
            },
            py: {
              xs: 1,
              sm: 1.5,
              md: 1.5,
            },
            fontSize: {
              xs: '0.875rem',
              sm: '1rem',
              md: '1rem',
            },
            pointerEvents: 'auto',
            '&:hover': {
              bgcolor: alpha('#ffffff', 0.9),
            },
          }}
        >
          {currentSlideData?.buttonText || 'Go to Course'}
        </Button>
      </Box>
    </Box>
  )
}
